import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Percent, BellRing, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { EtatHistorique } from './EtatHistorique';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/member-invitation`;
const eur = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export const RemisePibSetting = ({ getAuthHeader }) => {
  const [pct, setPct] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/settings/remise-pib`, { headers: getAuthHeader() }).then((r) => setPct(String(r.data.remise_pib_pct))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings/remise-pib`, { remise_pib_pct: Number(pct) }, { headers: getAuthHeader() });
      toast.success(`Remise ARCHOS (PIB) : ${r.data.remise_pib_pct} %`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50/50 px-3 py-2 mb-3" data-testid="remise-pib-setting">
      <Percent className="h-4 w-4 text-violet-600" />
      <span className="text-sm text-slate-700">Remise ARCHOS (PIB)</span>
      <Input type="number" min="0" max="100" step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} className="h-8 w-24" data-testid="remise-pib-input" />
      <span className="text-sm text-slate-500">%</span>
      <Button size="sm" onClick={save} disabled={saving || pct === ''} className="h-8 bg-violet-600 hover:bg-violet-700 text-white" data-testid="remise-pib-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
      </Button>
    </div>
  );
};

export const ContributionSuspensiveSetting = ({ getAuthHeader }) => {
  const [pct, setPct] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    axios.get(`${API}/admin/settings/contribution-suspensive`, { headers: getAuthHeader() }).then((r) => setPct(String(r.data.contribution_suspensive_pct))).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/settings/contribution-suspensive`, { contribution_suspensive_pct: Number(pct) }, { headers: getAuthHeader() });
      toast.success(`Contribution suspensive de réactivation : ${r.data.contribution_suspensive_pct} % des échéances dues`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); } finally { setSaving(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50/50 px-3 py-2 mb-3" data-testid="contribution-suspensive-setting">
      <Percent className="h-4 w-4 text-red-600" />
      <span className="text-sm text-slate-700">Contribution suspensive (réactivation après résiliation)</span>
      <Input type="number" min="0" max="200" step="1" value={pct} onChange={(e) => setPct(e.target.value)} className="h-8 w-24" data-testid="contribution-suspensive-input" />
      <span className="text-sm text-slate-500">% des échéances dues</span>
      <Button size="sm" onClick={save} disabled={saving || pct === ''} className="h-8 bg-red-600 hover:bg-red-700 text-white" data-testid="contribution-suspensive-save-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
      </Button>
    </div>
  );
};

const ETAT_LABEL = { partiel_valide: ['Paiement partiel · attestation valide', true], acquitte: ['Acquitté', true], impaye: ['Impayé · attestation invalide', false], resilie: ['Résilié', false] };

const Badge = ({ ok, children, testId }) => (
  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`} data-testid={testId}>{children}</span>
);

export const InvitationPaiements = ({ inv, getAuthHeader }) => {
  const p = inv.paiements;
  const [busy, setBusy] = useState(false);
  if (!p || !inv.membre_id) return null;
  const rappel = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/${inv.token}/rappel-echeance`, { origin: window.location.origin }, { headers: getAuthHeader() });
      toast.success(`Rappel de l'échéance ${r.data.echeance}/4 envoyé à ${r.data.email} (lien valable 7 jours)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Rappel impossible'); } finally { setBusy(false); }
  };
  const rcds = p.total_ttc == null ? 'RCDS : pas de souscription'
    : p.solde ? `RCDS ${p.mode === '4x' ? '4x soldé' : 'totalité'} : ${eur(p.paye_ttc)}`
      : p.mode === '4x' ? `Acompte/échéances ${p.nb_echeances_payees}/4 : ${eur(p.paye_ttc)} · reste ${eur(p.reste_ttc)}`
        : `RCDS non payée (${eur(p.total_ttc)})`;
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100" data-testid={`invitation-paiements-${inv.token}`}>
      <span className="text-[11px] text-slate-500">Paiements :</span>
      <Badge ok={p.ftpe_paye} testId={`inv-ftpe-${inv.token}`}>{p.ftpe_paye ? `FTPE payée ${eur(p.ftpe_montant)}` : 'FTPE non payée'}</Badge>
      <Badge ok={p.paye_ttc > 0} testId={`inv-rcds-${inv.token}`}>{rcds}</Badge>
      {ETAT_LABEL[p.etat_paiement] && (
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ETAT_LABEL[p.etat_paiement][1] ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}`} data-testid={`inv-etat-${inv.token}`}>{ETAT_LABEL[p.etat_paiement][0]}</span>
      )}
      {p.attestation_envoyee_at && <Badge ok testId={`inv-attestation-${inv.token}`}>Attestation envoyée</Badge>}
      <EtatHistorique items={p.etat_historique} testId={`inv-historique-${inv.token}`} />
      {p.mode === '4x' && !p.solde && (
        <Button size="sm" variant="outline" onClick={rappel} disabled={busy} className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700" data-testid={`inv-rappel-btn-${inv.token}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5" />} Rappel échéance
        </Button>
      )}
    </div>
  );
};
